import { useEffect, useRef, useState } from "react"

import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"
import { Share } from "lucide-react"

import { useNotebookSharing } from "../hooks/useNotebookSharing"
import {
  notebookSharePath,
  SHARING_AUDIENCES,
  type NotebookSharingScope,
  type SharingAudience,
} from "../utils/notebookSharing"

const LABELS: Record<SharingAudience, string> = {
  private: "Only me",
  workspace: "This workspace",
  organization: "This organization",
  public: "Anyone with the link",
}

export function NotebookShareDialog({
  open,
  onOpenChange,
  onSaved,
  onLoaded,
  ...scope
}: NotebookSharingScope & {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved?: (sharing: { audience: SharingAudience }) => void
  /** The audience Sofia reports, for callers whose notebook payload omits it. */
  onLoaded?: (sharing: { audience: SharingAudience }) => void
}) {
  const [draft, setDraft] = useState<SharingAudience | null>(null)
  const [copyMessage, setCopyMessage] = useState<string | null>(null)
  const { sharing, pending, saving, error, save } = useNotebookSharing(
    scope,
    open,
  )
  const onLoadedRef = useRef(onLoaded)
  onLoadedRef.current = onLoaded
  const loadedAudience = sharing?.audience
  useEffect(() => {
    if (loadedAudience) {
      onLoadedRef.current?.({ audience: loadedAudience })
    }
  }, [loadedAudience])
  const selected = draft ?? sharing?.audience ?? "private"
  const changed = !!sharing && selected !== sharing.audience
  const path = sharing ? notebookSharePath(scope, sharing) : null
  const isSeries =
    scope.targetKind === "chat-series" ||
    scope.targetKind === "generated-series"
  const supportsShareLink =
    scope.targetKind !== "generated" && scope.targetKind !== "generated-series"
  const copy = async () => {
    if (!path) {
      return
    }
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${path}`)
      setCopyMessage("Link copied")
    } catch {
      setCopyMessage("Could not copy the link. Please try again.")
    }
  }
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && saving) {
          return
        }
        if (!next) {
          setDraft(null)
          setCopyMessage(null)
        }
        onOpenChange(next)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isSeries ? "Share notebook series" : "Share notebook"}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <p className="text-sm text-coval-text-muted">
            {isSeries
              ? "Choose who can read the template and editions in this series. Future editions inherit this visibility. Only you can rename, delete, or change sharing."
              : supportsShareLink
                ? "Choose who can read this saved notebook using its link. Only you can rename, delete, or change sharing."
                : "Choose who can read this saved notebook. Only you can rename, delete, or change sharing."}
          </p>
          <Select
            value={selected}
            onValueChange={(value) => {
              setDraft(value as SharingAudience)
              setCopyMessage(null)
            }}
            disabled={pending || !sharing}
          >
            <SelectTrigger aria-label="Who can access this notebook">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SHARING_AUDIENCES.map((audience) => (
                <SelectItem key={audience} value={audience}>
                  {LABELS[audience]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selected === "public" && supportsShareLink ? (
            <p className="text-sm text-coval-text">
              Anyone with the link can read the saved text, tables, and charts
              without signing in. Check the content before making it public.
            </p>
          ) : null}
          {selected === "workspace" || selected === "organization" ? (
            <p className="text-sm text-coval-text">
              Recipients must sign in and have access to this {selected}.
              {supportsShareLink
                ? " Send them the link; it does not add the notebook to their personal list."
                : ""}
            </p>
          ) : null}
          <p className="text-sm text-coval-text-muted">
            Linked traces and simulations keep their own permissions.
            {supportsShareLink
              ? " Changing access replaces the link; choosing Only me revokes it."
              : ""}
          </p>
          {error ? (
            <p role="alert" className="text-sm text-coval-danger-text">
              {error}
            </p>
          ) : null}
          {path && !changed && open && (
            <label className="block space-y-2 text-sm">
              Share link
              <Input
                aria-label="Share link"
                readOnly
                value={`${window.location.origin}${path}`}
                onFocus={(event) => event.target.select()}
              />
            </label>
          )}
          {copyMessage ? (
            <p role="status" className="text-sm text-coval-text">
              {copyMessage}
            </p>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button
            variant="secondary"
            disabled={saving}
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
          {changed ? (
            <Button
              disabled={pending}
              onClick={() =>
                void save(selected).then((ok) => {
                  if (ok) {
                    setDraft(null)
                    setCopyMessage(null)
                    onSaved?.({ audience: selected })
                  }
                })
              }
            >
              {pending
                ? "Saving…"
                : selected === "public"
                  ? "Make public"
                  : "Save access"}
            </Button>
          ) : supportsShareLink ? (
            <Button
              disabled={!path || pending || !!error}
              onClick={() => void copy()}
            >
              Copy link
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function NotebookShareButton(
  scope: NotebookSharingScope & { audience?: SharingAudience },
) {
  const [open, setOpen] = useState(false)
  const [savedAudience, setSavedAudience] = useState<
    SharingAudience | undefined
  >(scope.audience)
  const [lastPropAudience, setLastPropAudience] = useState(scope.audience)
  if (scope.audience !== lastPropAudience) {
    setLastPropAudience(scope.audience)
    setSavedAudience(scope.audience)
  }
  const effectiveAudience = savedAudience ?? scope.audience
  const label = effectiveAudience
    ? `Share · ${LABELS[effectiveAudience]}`
    : "Share"
  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)}>
        <Share className="size-4" /> {label}
      </Button>
      <NotebookShareDialog
        {...scope}
        open={open}
        onOpenChange={setOpen}
        onSaved={({ audience }) => setSavedAudience(audience)}
        onLoaded={({ audience }) => setSavedAudience(audience)}
      />
    </>
  )
}
